import React from "react";
import { BadgeIcon, IconVariant } from "@/components/ui/badgeIcon";
import { BadgeProps } from "../types";
import { defaultBadges } from "../constants";
import { DEFAULT_MICRO_DEGREE_CONFIG, MICRO_DEGREE_TYPES } from "./badgeConfig";
import { MicroDegreeTag } from "@/monarch/rules/productCardRedesign/types";

export interface BadgeFactoryProps {
  uuid: string;
  monarchBadges?: BadgeProps[];
  popularItemList?: string[];
  programType?: string[];
  microDegreeTag?: {
    text: string;
    icon: IconVariant;
  };
  type: string;
  popularBadgeOverride?: MicroDegreeTag;
}

export const createMicroDegreeBadge = (
  props: BadgeFactoryProps,
): React.ReactElement | null => {
  const { microDegreeTag } = props;

  const isMicroDegreeType = MICRO_DEGREE_TYPES.includes(props.type);

  if (!isMicroDegreeType) {
    return null;
  }

  return (
    <BadgeIcon
      key="microDegree"
      icon={microDegreeTag?.icon || DEFAULT_MICRO_DEGREE_CONFIG.icon}
      iconSize={14}
    >
      {microDegreeTag?.text || DEFAULT_MICRO_DEGREE_CONFIG.text}
    </BadgeIcon>
  );
};

export const createPopularBadgeFromMonarchCustomSet = (
  props: BadgeFactoryProps,
): React.ReactElement | null => {
  const { uuid, popularItemList, monarchBadges } = props;

  const isPopular = popularItemList?.includes(uuid);
  if (!isPopular) {
    return null;
  }

  const popularBadgeText =
    props.popularBadgeOverride?.text ||
    monarchBadges?.find((badge) => badge.type === "popularBadge")?.text ||
    "Popular";
  const popularBadgeIcon =
    props.popularBadgeOverride?.icon ||
    monarchBadges?.find((badge) => badge.type === "popularBadge")?.icon ||
    "popular";

  return (
    <BadgeIcon key="popularBadge" icon={popularBadgeIcon} iconSize={14}>
      {popularBadgeText}
    </BadgeIcon>
  );
};

const getFromDefaultBadgesSet = (badgeType: string): BadgeProps | undefined => {
  return defaultBadges.find((badge) => badge.type === badgeType);
};

export const createMonarchBadge = (
  badgeType: string,
  props: BadgeFactoryProps,
): React.ReactElement | null => {
  const { monarchBadges, uuid, popularItemList } = props;

  if (badgeType === "popularBadge" && popularItemList?.includes(uuid)) {
    return null;
  }

  const defaultBadge = getFromDefaultBadgesSet(badgeType);
  let monarchBadge = monarchBadges?.find((badge) => badge.type === badgeType);

  let badgeToRender: BadgeProps | undefined;

  if (monarchBadge) {
    badgeToRender = {
      ...monarchBadge,
      text: monarchBadge.text || defaultBadge?.text || "",
      icon: monarchBadge.icon || defaultBadge?.icon || "none",
    };
  } else {
    badgeToRender = defaultBadge;
  }

  if (!badgeToRender) {
    return null;
  }

  return (
    <BadgeIcon key={badgeType} icon={badgeToRender.icon} iconSize={14}>
      {badgeToRender.text}
    </BadgeIcon>
  );
};
